<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { frontendURL } from 'dashboard/helper/URLHelper';
import SicargDashboardAPI from 'dashboard/api/sicargDashboard';
import NextButton from 'dashboard/components-next/button/Button.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import MentionsTable from './components/MentionsTable.vue';
import PriorityCasesView from './components/PriorityCasesView.vue';
import MonthStats from './components/MonthStats.vue';
import { humanizeSince } from './helpers';

// SICARG MOD 6 — Panel del agente: casos pendientes, menciones, prioridad y
// estadísticas del mes en curso, todo scopeado al agente logueado.
const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();

const isLoading = ref(false);
// Segmento por clasificación del último cierre ('' = todos los casos).
const SEGMENTS = ['', 'negociacion', 'compromiso_de_pago'];
const segment = ref('');
const metrics = ref({
  pending: 0,
  waiting: 0,
  unanswered: 0,
  oldest_waiting_since: null,
});
const conversations = ref([]);
const mentions = ref([]);
const priorityConversations = ref([]);
const monthStats = ref({});

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await SicargDashboardAPI.get(
      segment.value ? { closure_reason: segment.value } : {}
    );
    metrics.value = { ...metrics.value, ...(data.metrics || {}) };
    conversations.value = data.conversations || [];
    mentions.value = data.mentions || [];
    priorityConversations.value = data.priority_conversations || [];
    monthStats.value = data.month_stats || {};
  } catch (error) {
    useAlert(t('SICARG_DASHBOARD.API.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const selectSegment = value => {
  segment.value = value;
  load();
};

onMounted(load);

const handlePriorityCleared = conversationId => {
  priorityConversations.value = priorityConversations.value.filter(
    conversation => conversation.id !== conversationId
  );
};

const handleMentionDismissed = mentionId => {
  mentions.value = mentions.value.filter(mention => mention.id !== mentionId);
};

const openConversation = conversation => {
  router.push(
    frontendURL(`accounts/${accountId.value}/conversations/${conversation.id}`)
  );
};
</script>

<template>
  <section class="flex overflow-auto flex-col gap-6 p-6 w-full h-full bg-n-background">
    <header class="flex justify-between items-center">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('SICARG_DASHBOARD.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11">
          {{ t('SICARG_DASHBOARD.DESCRIPTION') }}
        </p>
      </div>
      <NextButton
        faded
        slate
        sm
        icon="i-lucide-refresh-cw"
        :is-loading="isLoading"
        :label="t('SICARG_DASHBOARD.REFRESH')"
        @click="load"
      />
    </header>

    <!-- Métricas -->
    <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
      <div class="p-4 rounded-lg border border-n-weak">
        <p class="text-sm text-n-slate-11">
          {{ t('SICARG_DASHBOARD.METRICS.PENDING') }}
        </p>
        <p class="text-3xl font-semibold text-n-slate-12">
          {{ metrics.pending }}
        </p>
      </div>
      <div class="p-4 rounded-lg border border-n-weak">
        <p class="text-sm text-n-slate-11">
          {{ t('SICARG_DASHBOARD.METRICS.WAITING') }}
        </p>
        <p class="text-3xl font-semibold text-n-slate-12">
          {{ metrics.waiting }}
        </p>
      </div>
      <div class="p-4 rounded-lg border border-n-weak">
        <p class="text-sm text-n-slate-11">
          {{ t('SICARG_DASHBOARD.METRICS.UNANSWERED') }}
        </p>
        <p class="text-3xl font-semibold text-n-slate-12">
          {{ metrics.unanswered }}
        </p>
      </div>
      <div class="p-4 rounded-lg border border-n-weak">
        <p class="text-sm text-n-slate-11">
          {{ t('SICARG_DASHBOARD.METRICS.OLDEST_WAITING') }}
        </p>
        <p class="text-3xl font-semibold text-n-slate-12">
          {{ humanizeSince(metrics.oldest_waiting_since) }}
        </p>
      </div>
    </div>

    <!-- Casos abiertos (centro, destacado) + resumen del mes / prioridad / menciones (lateral) -->
    <div class="grid grid-cols-1 gap-6 items-start lg:grid-cols-3">
      <CardLayout class="lg:col-span-2">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('SICARG_DASHBOARD.LIST.TITLE') }}
        </h2>
        <div class="flex gap-2">
          <NextButton
            v-for="value in SEGMENTS"
            :key="value || 'all'"
            sm
            :faded="segment !== value"
            :solid="segment === value"
            :slate="segment !== value"
            :blue="segment === value"
            :label="t(`SICARG_DASHBOARD.SEGMENTS.${(value || 'all').toUpperCase()}`)"
            @click="selectSegment(value)"
          />
        </div>
        <div class="rounded-lg border divide-y border-n-weak divide-n-weak">
          <button
            v-for="conversation in conversations"
            :key="conversation.id"
            class="flex justify-between items-center px-4 py-3 w-full text-left hover:bg-n-alpha-1"
            @click="openConversation(conversation)"
          >
            <div class="flex flex-col">
              <span class="text-sm font-medium text-n-slate-12">
                {{ conversation.contact?.name || t('SICARG_DASHBOARD.LIST.NO_NAME') }}
                <span class="ml-1 text-xs text-n-slate-10">#{{ conversation.id }}</span>
              </span>
              <span v-if="conversation.numero_cuenta" class="text-xs text-n-slate-11">
                {{ t('SICARG_DASHBOARD.LIST.ACCOUNT') }}: {{ conversation.numero_cuenta }}
              </span>
            </div>
            <div class="flex flex-col items-end">
              <span class="text-xs text-n-slate-11">
                {{ t('SICARG_DASHBOARD.LIST.WAITING') }}
              </span>
              <span class="text-sm font-medium text-n-amber-11">
                {{ humanizeSince(conversation.waiting_since || conversation.last_activity_at) }}
              </span>
            </div>
          </button>
          <p
            v-if="!conversations.length"
            class="px-4 py-6 text-sm text-center text-n-slate-10"
          >
            {{ t('SICARG_DASHBOARD.LIST.EMPTY') }}
          </p>
        </div>
      </CardLayout>

      <div class="flex flex-col gap-6">
        <MonthStats :stats="monthStats" />

        <PriorityCasesView
          :conversations="priorityConversations"
          @cleared="handlePriorityCleared"
        />

        <MentionsTable :mentions="mentions" @dismissed="handleMentionDismissed" />
      </div>
    </div>
  </section>
</template>
